<template>
  <!-- F07 DSD: แบบประเมินความพึงพอใจหลักสูตร (DSD-07) -->
  <v-dialog
    :value="show"
    persistent
    width="640"
    scrollable
    :fullscreen="$vuetify.breakpoint.xsOnly"
  >
    <v-card
      data-testid="course-evaluation-dialog"
      :tile="$vuetify.breakpoint.xsOnly"
      :style="
        $vuetify.breakpoint.xsOnly
          ? { borderRadius: '0' }
          : { borderRadius: '16px' }
      "
    >
      <!-- (1) หัวคงที่ -->
      <v-card-title class="d-block eval-head">
        <div class="d-flex align-start">
          <span class="eval-title">{{ $t("courseevaluation") }}</span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            :aria-label="$t('evallater')"
            :disabled="submitting"
            @click="onClose"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <p class="eval-course-name">{{ course.courseName }}</p>
        <div class="eval-notice">
          <v-icon small color="#424242" class="mr-2"
            >mdi-account-eye-outline</v-icon
          >
          <span>{{ $t("evalnamevisible") }}</span>
        </div>
      </v-card-title>
      <v-divider></v-divider>
      <!-- (2) ส่วนเลื่อนได้ -->
      <v-card-text ref="evalBody" class="pt-4">
        <p class="eval-intro">{{ $t("courseevaluationintro") }}</p>
        <div v-for="(t, i) in topics" :key="t.key" class="mb-4">
          <p :id="'eval-label-' + t.key" class="eval-topic-label">
            {{ i + 1 + ". " + $t(t.label) }}
          </p>
          <v-btn-toggle
            v-model="scores[t.key]"
            role="group"
            :aria-labelledby="'eval-label-' + t.key"
            active-class="eval-score-active"
            class="eval-toggle"
          >
            <v-btn
              v-for="n in 5"
              :key="n"
              :value="n"
              min-width="48"
              height="44"
              :aria-pressed="String(scores[t.key] === n)"
              :aria-label="$t(t.label) + ' ' + n"
              :data-testid="'eval-' + t.key + '-' + n"
              class="eval-score-btn"
              >{{ n }}</v-btn
            >
          </v-btn-toggle>
          <div class="eval-scale">
            <span>{{ $t("evalscaleleast") }}</span>
            <span>{{ $t("evalscalemost") }}</span>
          </div>
        </div>
        <v-textarea
          v-model="comment"
          outlined
          :label="$t('evalcomment')"
          counter="1000"
          maxlength="1000"
          auto-grow
          rows="3"
          :disabled="submitting"
        ></v-textarea>
        <v-alert
          v-if="errorKey"
          ref="evalError"
          dense
          type="error"
          text
          role="alert"
          >{{ $t(errorKey) }}</v-alert
        >
      </v-card-text>
      <v-divider></v-divider>
      <!-- (3) ท้ายคงที่ -->
      <v-card-actions class="d-block px-4 py-3">
        <div aria-live="polite" class="eval-missing">
          <span v-if="!allAnswered">{{
            $t("evalmissingtopics", { items: missingItems.join(", ") })
          }}</span>
        </div>
        <div class="d-flex justify-end align-center flex-nowrap eval-actions">
          <v-btn
            text
            height="44"
            class="text-capitalize eval-action-btn"
            :disabled="submitting"
            @click="onClose"
            >{{ $t("evallater") }}</v-btn
          >
          <v-btn
            color="#A6CC39"
            height="44"
            class="text-capitalize white--text eval-action-btn"
            style="border-radius: 8px"
            :disabled="!allAnswered || submitting"
            :loading="submitting"
            @click="submitEvaluation"
            >{{ $t("evalsubmit") }}</v-btn
          >
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// F07 DSD — แบบประเมินความพึงพอใจหลักสูตร (v7 CourseEvaluation/SubmitCourseEvaluation)
export default {
  name: "CourseEvaluationDialog",
  props: {
    show: { type: Boolean, default: false },
    course: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      // ลำดับคงที่ — key = ชื่อ field API ตัด "Score" และ = <topic> ของ data-testid
      topics: [
        { key: "content", label: "evaltopiccontent" },
        { key: "instructor", label: "evaltopicinstructor" },
        { key: "mediaDisplay", label: "evaltopicmediadisplay" },
        { key: "supportingMaterial", label: "evaltopicmaterial" },
        { key: "systemUsability", label: "evaltopicsystem" },
        { key: "overall", label: "evaltopicoverall" },
      ],
      scores: {
        content: null,
        instructor: null,
        mediaDisplay: null,
        supportingMaterial: null,
        systemUsability: null,
        overall: null,
      },
      comment: "",
      submitting: false,
      errorKey: "",
    };
  },
  computed: {
    missingItems() {
      const out = [];
      this.topics.forEach((t, i) => {
        const v = this.scores[t.key];
        if (!(v >= 1 && v <= 5)) out.push(i + 1);
      });
      return out;
    },
    allAnswered() {
      return this.missingItems.length === 0;
    },
  },
  watch: {
    show(val) {
      if (val) this.resetForm();
    },
  },
  methods: {
    emptyScores() {
      return {
        content: null,
        instructor: null,
        mediaDisplay: null,
        supportingMaterial: null,
        systemUsability: null,
        overall: null,
      };
    },
    resetForm() {
      this.scores = this.emptyScores();
      this.comment = "";
      this.errorKey = "";
      this.submitting = false;
      this.$nextTick(() => {
        const b = this.$refs.evalBody;
        const el = b && (b.$el || b);
        if (el) el.scrollTop = 0;
      });
    },
    onClose() {
      if (this.submitting) return;
      this.$emit("close");
    },
    async submitEvaluation() {
      if (!this.allAnswered || this.submitting) return;
      this.submitting = true;
      this.errorKey = "";
      const s = this.scores;
      const request = {
        userID: Number.parseInt(localStorage.getItem("UserID")),
        companyID: localStorage.getItem("companyID"),
        assignmentCourseID: this.course.assignmentCourseID,
        contentScore: s.content,
        instructorScore: s.instructor,
        mediaDisplayScore: s.mediaDisplay,
        supportingMaterialScore: s.supportingMaterial,
        systemUsabilityScore: s.systemUsability,
        overallScore: s.overall,
        comment: this.comment,
      };
      let result = null; // "ok" | "already" | error key
      try {
        const res = await this.$axios.post(
          `${this.web_urlV7}CourseEvaluation/SubmitCourseEvaluation`,
          request,
          { timeout: 15000 }
        );
        const data = res.data ? res.data.data : null;
        const reason = data ? data.reason : null;
        if (res.data.status == 0) result = "ok";
        else if (res.data.status == 1 && reason === "AlreadySubmitted")
          result = "already";
        else if (res.data.status == 1 && reason === "NotCompleted")
          result = "evalnotcompleted";
        else result = "evalsubmitfailed"; // InvalidData / NotEnabled / status 2
        if (result === "ok" || result === "already") {
          this.submitting = false;
          this.$emit("submitted", {
            assignmentCourseID: this.course.assignmentCourseID,
            submitDate: result === "ok" && data ? data.submitDate : null,
            already: result === "already",
          });
          return;
        }
      } catch (e) {
        result = "evalsubmitfailed"; // HTTP 400/401/timeout/network
      }
      this.submitting = false;
      this.errorKey = result; // คำตอบ/ความเห็นไม่ล้าง
      this.$nextTick(() => {
        const a = this.$refs.evalError;
        const el = a && (a.$el || a);
        if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
      });
    },
  },
};
</script>

<style scoped>
.eval-title {
  font-size: 20px;
  color: #363636;
  word-break: normal;
  overflow-wrap: anywhere;
  line-height: 1.4;
  padding-top: 6px;
}
.eval-course-name {
  font-weight: bold;
  font-size: 16px;
  color: #424242;
  margin: 4px 0 8px;
  word-break: normal;
  overflow-wrap: anywhere;
  line-height: 1.5;
}
.eval-notice {
  display: flex;
  align-items: flex-start;
  background: #f5f5f5;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: normal;
  color: #424242;
  line-height: 1.5;
  word-break: normal;
}
.eval-intro {
  font-size: 14px;
  color: #424242;
}
.eval-topic-label {
  font-weight: bold;
  font-size: 16px;
  color: #424242;
  margin-bottom: 8px;
}
.eval-toggle {
  background: transparent !important;
  box-shadow: none;
}
::v-deep .eval-toggle > .v-btn.v-btn {
  background-color: #fff;
  border: 1px solid #c0c0c0 !important;
  color: #424242;
  opacity: 1;
}
::v-deep .eval-toggle > .v-btn.eval-score-active {
  background-color: #a6cc39 !important;
  color: #fff !important;
  font-weight: bold;
}
::v-deep .eval-toggle > .v-btn.eval-score-active::before {
  opacity: 0 !important;
}
.eval-scale {
  width: 240px;
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #616161;
  margin-top: 4px;
}
.eval-missing {
  font-size: 14px;
  color: #424242;
  min-height: 20px;
  margin-bottom: 8px;
  text-align: right;
}
.eval-actions {
  gap: 8px;
}
.eval-action-btn {
  min-width: 0 !important;
}
::v-deep .eval-action-btn .v-btn__content {
  white-space: normal;
}
</style>
